import React, { useMemo } from 'react';
import { pricesWrapper } from './styles';
import { ProductPriceType } from './types';
import { useNumberFormatter } from 'common/hooks';
import { ProductAdditionalPrice } from './product-additional-price';
import { ProductPrice } from './product-price';
import { ProductSpecialPrice } from './product-special-price';

type ProductPriceToDisplayProps = {
  hasOldPrice: boolean;
  uom?: string;
  price: ProductPriceType;
  productPriceCategory: 'STD' | 'LOF' | 'BPR' | 'PIM' | 'WIZ';
};

export const ProductPriceToDisplay: React.FC<ProductPriceToDisplayProps> = ({
  hasOldPrice,
  price,
  productPriceCategory,
}) => {
  const formatter = useNumberFormatter({
    options: { currency: price.currency, currencyDisplay: 'symbol', style: 'currency' },
  });

  const formattedPrice = useMemo(() => formatter.format(price.main_price), [formatter, price.main_price]);
  const formattedOldPrice = useMemo(() => formatter.format(price.previous_price || 0), [
    formatter,
    price.previous_price,
  ]);

  const [rawPrice, finalCurrency] = useMemo(() => {
    const indexOfCurrency = formattedPrice.indexOf('₽');
    return [formattedPrice.slice(0, indexOfCurrency), formattedPrice.slice(indexOfCurrency)];
  }, [formattedPrice]);

  const { finalPriceFirst, finalPriceSecond } = useMemo(() => {
    const pricesArr = rawPrice.split(',');
    const hasComma = Boolean(parseInt(pricesArr[1], 10));
    const mainPrice = pricesArr[0];
    const fractPrice = hasComma ? pricesArr[1] : null;
    return { finalPriceFirst: mainPrice, finalPriceSecond: fractPrice };
  }, [rawPrice]);

  const hasBestPrice = productPriceCategory === 'BPR';
  const hasSpecialPrice = hasOldPrice || hasBestPrice;

  return (
    <div className={pricesWrapper}>
      {hasSpecialPrice ? (
        <ProductSpecialPrice
          uom={price.main_uom_rus}
          rawOldPrice={formattedOldPrice}
          currency={finalCurrency}
          priceFirst={finalPriceFirst}
          priceSecond={finalPriceSecond}
          hasBestPrice={hasBestPrice}
          hasOldPrice={hasOldPrice}
        />
      ) : (
        <ProductPrice
          uom={price.main_uom_rus}
          priceFirst={finalPriceFirst}
          priceSecond={finalPriceSecond}
          currency={finalCurrency}
        />
      )}
      {price.additional_price && price.additional_uom && (
        <ProductAdditionalPrice
          uom={price.additional_uom_rus}
          rawAdditionalPrice={price.additional_price}
          currency={finalCurrency}
        />
      )}
    </div>
  );
};
